
(function () {
  "use strict";

  // LIVE STORE: Keep this set to false.
  const TEST_MODE = false;

  const STORAGE_KEY = "modefinity_attribution_v1";
  const PIN_KEY = "modefinity_pinterest_click_v1";

  const RETENTION_MS = 90 * 86400000;
  const CLICK_RETENTION_MS = 30 * 86400000;

  const CAMPAIGN_FIELDS = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_content",
    "utm_term",
    "pins_campaign_id"
  ];

  const CLICK_FIELDS = [
    "epik",
    "fbclid",
    "gclid",
    "gbraid",
    "wbraid"
  ];

  let syncing = false;
  let pendingSync = false;
  let syncTimer = null;

  function consentAllowed() {
    if (TEST_MODE) return true;

    try {
      const privacy = window.Shopify?.customerPrivacy;

      return !!(
        privacy &&
        privacy.analyticsProcessingAllowed() &&
        privacy.marketingAllowed()
      );
    } catch (_) {
      return false;
    }
  }

  function getCookie(name) {
    try {
      const prefix = name + "=";

      const found = document.cookie
        .split("; ")
        .find(item => item.startsWith(prefix));

      return found
        ? decodeURIComponent(found.slice(prefix.length))
        : "";
    } catch (_) {
      return "";
    }
  }

  function readJSON(key) {
    try {
      return JSON.parse(
        localStorage.getItem(key) || "null"
      );
    } catch (_) {
      return null;
    }
  }

  function storeJSON(key, value) {
    try {
      localStorage.setItem(
        key,
        JSON.stringify(value)
      );
    } catch (_) {}
  }

  function readAttribution() {
    if (!consentAllowed()) return null;

    const stored = readJSON(STORAGE_KEY);

    if (
      !stored?.first_touch ||
      !Number.isFinite(stored.first_touch.timestamp)
    ) {
      return null;
    }

    if (
      Date.now() - stored.first_touch.timestamp >
      RETENTION_MS
    ) {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (_) {}

      return null;
    }

    return stored;
  }

  function detectSource(params) {
    const values = {};

    [...CAMPAIGN_FIELDS, ...CLICK_FIELDS].forEach(
      function (key) {
        const value = params.get(key);

        if (value && value.length <= 512) {
          values[key] = value;
        }
      }
    );

    const referrer = document.referrer || "";
    let externalHost = "";

    try {
      const url = new URL(referrer);

      if (url.hostname !== location.hostname) {
        externalHost = url.hostname.toLowerCase();
      }
    } catch (_) {}

    let source = values.utm_source || "";
    let medium = values.utm_medium || "";

    if (!source) {
      if (values.epik) {
        source = "pinterest";
        medium = medium || "paid";
      } else if (values.fbclid) {
        source = "facebook";
        medium = medium || "paid";
      } else if (
        values.gclid ||
        values.gbraid ||
        values.wbraid
      ) {
        source = "google";
        medium = medium || "paid";
      } else if (externalHost) {
        if (
          /(^|\.)google\./.test(externalHost)
        ) {
          source = "google";
          medium = "organic";
        } else if (
          /(^|\.)bing\.com$/.test(externalHost)
        ) {
          source = "bing";
          medium = "organic";
        } else if (
          /(^|\.)pinterest\./.test(externalHost)
        ) {
          source = "pinterest";
          medium = "organic";
        } else if (
          /(^|\.)facebook\.com$/.test(externalHost) ||
          /(^|\.)instagram\.com$/.test(externalHost) ||
          /(^|\.)l\.facebook\.com$/.test(externalHost)
        ) {
          source = externalHost.includes("instagram")
            ? "instagram"
            : "facebook";

          medium = "referral";
        } else {
          source = externalHost;
          medium = "referral";
        }
      }
    }

    if (!source && Object.keys(values).length) {
      source = "unknown";
    }

    if (!source) {
      source = "direct";
      medium = "none";
    }

    return {
      ...values,
      utm_source: source,
      utm_medium: medium,
      landing_page: location.href,
      timestamp: Date.now()
    };
  }

  function capturePinterestClick(params) {
    const clickId =
      params.get("epik") ||
      getCookie("_epik") ||
      "";

    if (!clickId || clickId.length > 512) {
      return;
    }

    const previous = readJSON(PIN_KEY);

    if (
      previous?.click_id === clickId &&
      Number.isFinite(previous.timestamp) &&
      Date.now() - previous.timestamp <
        CLICK_RETENTION_MS
    ) {
      return;
    }

    storeJSON(PIN_KEY, {
      click_id: clickId,
      timestamp: Date.now()
    });
  }

  function captureAttribution() {
    if (!consentAllowed()) {
      return false;
    }

    const params = new URLSearchParams(
      location.search
    );

    capturePinterestClick(params);

    const touch = detectSource(params);

    const stored = readAttribution() || {
      first_touch: null,
      last_touch: null
    };

    if (!stored.first_touch) {
      stored.first_touch = touch;
    }

    const previous = stored.last_touch;

    // Direct visits do not overwrite the
    // last identifiable marketing source.
    if (
      touch.utm_source !== "direct" ||
      !previous
    ) {
      const comparable = [
        "utm_source",
        "utm_medium",
        "utm_campaign",
        "utm_content",
        "utm_term",
        ...CLICK_FIELDS
      ];

      const changed =
        !previous ||
        comparable.some(function (key) {
          return (
            (previous[key] || "") !==
            (touch[key] || "")
          );
        });

      if (changed) {
        stored.last_touch = touch;
      }
    }

    storeJSON(STORAGE_KEY, stored);

    return true;
  }

  function dateString(timestamp) {
    return Number.isFinite(timestamp)
      ? new Date(timestamp).toISOString()
      : "";
  }

  function buildCartAttributes(stored) {
    const first = stored.first_touch;
    const last =
      stored.last_touch || first;

    const pinterest = readJSON(PIN_KEY);

    const pinterestValid =
      pinterest?.click_id &&
      Number.isFinite(pinterest.timestamp) &&
      Date.now() - pinterest.timestamp <
        CLICK_RETENTION_MS;

    return {
      "Attribution First Source":
        first.utm_source || "",

      "Attribution First Medium":
        first.utm_medium || "",

      "Attribution First Campaign":
        first.utm_campaign || "",

      "Attribution First Content":
        first.utm_content || "",

      "Attribution First Date":
        dateString(first.timestamp),

      "Attribution Last Source":
        last.utm_source || "",

      "Attribution Last Medium":
        last.utm_medium || "",

      "Attribution Last Campaign":
        last.utm_campaign || "",

      "Attribution Last Content":
        last.utm_content || "",

      "Attribution Last Date":
        dateString(last.timestamp),

      "Attribution Pinterest Campaign ID":
        last.pins_campaign_id ||
        first.pins_campaign_id ||
        "",

      "Attribution Pinterest Click ID":
        pinterestValid
          ? pinterest.click_id
          : "",

      "Attribution Pinterest Click Date":
        pinterestValid
          ? dateString(pinterest.timestamp)
          : "",

      "Attribution Google Click ID":
        last.gclid || first.gclid || "",

      "Attribution Google Gbraid":
        last.gbraid || first.gbraid || "",

      "Attribution Google Wbraid":
        last.wbraid || first.wbraid || "",

      "Attribution Meta Click ID":
        last.fbclid || first.fbclid || ""
    };
  }

  function cartRoot() {
    return window.Shopify?.routes?.root || "/";
  }

  async function syncAttributionToCart() {
    if (!consentAllowed()) return;

    if (syncing) {
      pendingSync = true;
      return;
    }

    const stored = readAttribution();

    if (!stored) return;

    syncing = true;

    try {
      const attributes =
        buildCartAttributes(stored);

      // Check attributes currently saved
      // in the Shopify cart.
      const cartResponse = await fetch(
        cartRoot() + "cart.js",
        {
          credentials: "same-origin"
        }
      );

      if (!cartResponse.ok) {
        throw new Error(
          "Cart read failed: " +
          cartResponse.status
        );
      }

      const cart = await cartResponse.json();

      const current = cart.attributes || {};

      const needsUpdate = Object.keys(
        attributes
      ).some(function (key) {
        return (
          String(current[key] || "") !==
          String(attributes[key] || "")
        );
      });

      if (!needsUpdate) return;

      const response = await fetch(
        cartRoot() + "cart/update.js",
        {
          method: "POST",
          credentials: "same-origin",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            attributes: attributes
          })
        }
      );

      if (!response.ok) {
        throw new Error(
          "Cart update failed: " +
          response.status
        );
      }

    } catch (error) {
      console.warn(
        "[Modefinity Attribution]",
        error
      );
    } finally {
      syncing = false;

      if (pendingSync) {
        pendingSync = false;
        scheduleSync(300);
      }
    }
  }

  function scheduleSync(delay = 500) {
    if (syncTimer) {
      clearTimeout(syncTimer);
    }

    syncTimer = setTimeout(
      function () {
        syncTimer = null;
        syncAttributionToCart();
      },
      delay
    );
  }

  function initialize() {
    if (!consentAllowed()) return;

    captureAttribution();
    scheduleSync();
  }

  // Initial visit
  initialize();

  // Shopify consent updates
  document.addEventListener(
    "visitorConsentCollected",
    initialize
  );

  // Browser back/forward navigation
  window.addEventListener(
    "pageshow",
    initialize
  );

  // Recheck after standard Add to Cart forms.
  document.addEventListener(
    "submit",
    function (event) {
      if (
        event.target.matches(
          'form[action*="/cart/add"]'
        )
      ) {
        scheduleSync(1800);
      }
    },
    true
  );

  // Recheck when visitor returns to the tab.
  document.addEventListener(
    "visibilitychange",
    function () {
      if (!document.hidden) {
        initialize();
      }
    }
  );

  // Wait for Shopify privacy API if necessary.
  let attempts = 0;

  const timer = setInterval(
    function () {
      attempts++;

      if (consentAllowed()) {
        clearInterval(timer);
        initialize();
      } else if (attempts >= 40) {
        clearInterval(timer);
      }
    },
    500
  );

})();
